import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithCredential,
  sendPasswordResetEmail
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAt,
  endAt,
  runTransaction,
  arrayUnion
} from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';
import { refreshSecurityToken } from './securityClaims';
import { AppUser, LoginMethod } from '../types';
import { searchUsers as sharedSearchUsers } from './searchService';
import { buildSearchIndex } from './searchIndex';
import { logAuthSession, logDeviceInstallation } from './deviceService';
import { withReadCounter } from '../utils/withReadCounter';
import { fallbackUsername } from '../utils/username';

export { fallbackUsername } from '../utils/username';

const ADMIN_EMAIL = 'ajaytripathi821@gmail.com';
const LOCAL_SESSION_KEY = 'clue200_user_session';
const LOCAL_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LOGIN_RECORDED_SESSION_KEY = 'yetrykar_login_recorded_uid';
const loginRecordedInMemory = new Set<string>();
const pendingLoginRecords = new Map<string, Promise<void>>();

// In-memory OTP storage for OTP flows
const pendingOtps: Record<string, { code: string; expiresAt: number; target: string }> = {};

/**
 * Shared search implementation used by Web and Android clients.
 */
export const searchUsers = async (searchTerm: string): Promise<AppUser[]> => sharedSearchUsers(searchTerm);

export const normalizeUsername = (username: string): string =>
  username.trim().replace(/^@+/, '').toLowerCase();

export const checkUsernameAvailability = async (
  username: string,
  currentUid?: string
): Promise<boolean> => {
  const normalized = normalizeUsername(username);
  if (!/^[a-z0-9][a-z0-9._-]{2,29}$/.test(normalized)) return false;

  const claim = await getDoc(doc(db, 'usernames', normalized));
  if (claim.exists() && claim.data().uid !== currentUid) return false;

  const existingUsers = await getDocs(query(collection(db, 'users'), where('username', '==', normalized), limit(1)));
  return existingUsers.empty || existingUsers.docs[0].id === currentUid;
};

export const claimUsername = async (username: string, uid: string): Promise<string> => {
  const normalized = normalizeUsername(username);
  if (!/^[a-z0-9][a-z0-9._-]{2,29}$/.test(normalized)) {
    throw new Error('Username must be 3-30 characters and use only letters, numbers, dots, underscores, or hyphens.');
  }

  const claimRef = doc(db, 'usernames', normalized);
  await runTransaction(db, async transaction => {
    const existing = await transaction.get(claimRef);
    if (existing.exists() && existing.data().uid !== uid) {
      throw new Error('That username is already taken.');
    }
    transaction.set(claimRef, { uid, username: normalized, updatedAt: serverTimestamp() }, { merge: true });
  });
  return normalized;
};

/**
 * Check if email is an admin
 */
export const isUserAdmin = (email?: string | null, role?: string): boolean => {
  if (!email) return false;
  return email.toLowerCase() === ADMIN_EMAIL.toLowerCase() || role === 'admin' || role === 'superadmin' || role === 'subadmin';
};

export const isAdminRole = (role?: string | null, email?: string | null): boolean => {
  const normalizedEmail = email?.toLowerCase();
  return normalizedEmail === ADMIN_EMAIL.toLowerCase() || role === 'superadmin' || role === 'subadmin';
};

export const isSuperAdmin = (email?: string | null, role?: string | null): boolean => {
  return email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() || role === 'superadmin';
};

import { generateAutoClueId } from './adminService';

/**
 * Save user profile to Firestore with History Tracking (Section 5)
 */
export const syncUserProfileToFirestore = async (user: AppUser, throwOnError = false): Promise<void> => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const snap = await withReadCounter(
      'auth',
      () => getDoc(userRef),
      value => ({ reads: value.metadata.fromCache ? 0 : 1 })
    );
    const existing = snap.exists() ? (snap.data() as AppUser) : null;
    const existingIsSuspended = existing?.suspension?.active === true ||
      existing?.isSuspended === true ||
      existing?.status === 'suspended';

    const profileData: any = {
      ...user,
      phone: user.phone ?? user.phoneNumber ?? null,
      profileComplete: user.profileComplete ?? false,
      role: existing?.role || user.role || 'user',
      status: existingIsSuspended ? 'suspended' : (existing?.status || 'active'),
      isSuspended: existingIsSuspended,
      updatedAt: serverTimestamp(),
      usernameLower: user.username ? normalizeUsername(user.username) : null,
      displayNameLower: user.displayName ? user.displayName.trim().toLowerCase() : null,
      emailLower: user.email ? user.email.trim().toLowerCase() : null,
      channelName: user.username ? `${user.displayName || user.username}'s Channel` : null,
    };
    const profilePhotoURL = user.profilePhotoURL || existing?.profilePhotoURL;
    if (profilePhotoURL) {
      profileData.profilePhotoURL = profilePhotoURL;
      profileData.photoURL = profilePhotoURL;
    }

    // Track Name Updates
    if (existing && user.displayName !== existing.displayName) {
        profileData.updated_name = {
            value: user.displayName,
            updatedAt: new Date().toISOString()
        };
        profileData.history = existing.history || {};
        profileData.history.displayName = arrayUnion({
            value: existing.displayName,
            updatedAt: existing.updated_name?.updatedAt || existing.createdAt
        });
    }

    // Track Mobile Updates
    const newPhone = user.phone || user.phoneNumber;
    const oldPhone = existing?.phone || existing?.phoneNumber;
    if (existing && newPhone && newPhone !== oldPhone) {
        profileData.updated_mobileNumber = {
            value: newPhone,
            updatedAt: new Date().toISOString()
        };
        profileData.history = existing.history || profileData.history || {};
        profileData.history.phone = arrayUnion({
            value: oldPhone,
            updatedAt: existing.updated_mobileNumber?.updatedAt || existing.createdAt
        });
    }

    if (!existing) {
        profileData.createdAt = serverTimestamp();
    }

    const sanitizedProfile = Object.fromEntries(
      Object.entries(profileData).filter(([, value]) => value !== undefined)
    );
    delete sanitizedProfile.followerCount;
    delete sanitizedProfile.followingCount;
    delete sanitizedProfile.fansCount;
    delete sanitizedProfile.followersCount;
    delete sanitizedProfile.videosCount;
    delete sanitizedProfile.postsCount;
    delete sanitizedProfile.totalPosts;
    delete sanitizedProfile.totalShorts;
    delete sanitizedProfile.totalLongs;
    delete sanitizedProfile.totalLives;
    delete sanitizedProfile.publishedContentCount;
    delete sanitizedProfile.totalViews;
    delete sanitizedProfile.totalLikes;
    delete sanitizedProfile.totalDislikes;
    delete sanitizedProfile.stats;

    if (existing) {
      delete sanitizedProfile.username;
      delete sanitizedProfile.uid;
      delete sanitizedProfile.email;
      delete sanitizedProfile.clueId;
      delete sanitizedProfile.role;
      delete sanitizedProfile.permissions;
      delete sanitizedProfile.suspension;
    }

    await withReadCounter('auth', () => setDoc(userRef, sanitizedProfile, { merge: true }), { writes: 1 });
    const publicProfile = {
      uid: user.uid,
      displayName: user.displayName,
      username: user.username || null,
      photoURL: profilePhotoURL || user.photoURL || null,
      bio: user.bio || null,
      city: user.city || null,
      state: user.state || null,
      country: user.country || null,
      clueId: user.clueId || null,
      channelName: user.username ? `${user.displayName || user.username}'s Channel` : null,
      searchKeywords: buildSearchIndex(`${user.displayName || ''} ${user.username || ''} ${user.clueId || ''}`)
    };
    await withReadCounter('auth', () => setDoc(doc(db, 'publicProfiles', user.uid), publicProfile, { merge: true }), { writes: 1 });

    // Assign ClueID if missing
    if (!existing?.clueId && !user.clueId) {
        await generateAutoClueId(user.uid);
    }

  } catch (error) {
    console.error('Firestore profile sync failed:', error);
    if (throwOnError) throw error;
  }
};

export const ensureUserProfileDocuments = async (user: AppUser): Promise<void> => {
  const userRef = doc(db, 'users', user.uid);
  const publicProfileRef = doc(db, 'publicProfiles', user.uid);
  const [userSnapshot, publicProfileSnapshot] = await Promise.all([
    getDoc(userRef),
    getDoc(publicProfileRef)
  ]);
  if (userSnapshot.exists() && publicProfileSnapshot.exists()) return;

  if (!userSnapshot.exists()) {
    await syncUserProfileToFirestore(user, true);
  } else {
    const profile = userSnapshot.data();
    await setDoc(publicProfileRef, {
      uid: user.uid,
      displayName: String(profile.displayName || user.displayName || 'Clue200 Member'),
      username: profile.username || user.username || null,
      photoURL: profile.profilePhotoURL || profile.photoURL || user.photoURL || null,
      bio: profile.bio || user.bio || null,
      city: profile.city || user.city || null,
      state: profile.state || user.state || null,
      country: profile.country || user.country || null,
      clueId: profile.clueId || user.clueId || null,
      channelName: profile.channelName || (profile.username
        ? `${profile.displayName || user.displayName || profile.username}'s Channel`
        : null),
      searchKeywords: buildSearchIndex(`${profile.displayName || user.displayName || ''} ${profile.username || user.username || ''} ${profile.clueId || user.clueId || ''}`),
      followerCount: Number(profile.followerCount) || 0,
      followingCount: Number(profile.followingCount) || 0,
      publishedContentCount: Number(profile.publishedContentCount) || 0
    });
  }

  const [createdUserSnapshot, createdPublicProfileSnapshot] = await Promise.all([
    getDoc(userRef),
    getDoc(publicProfileRef)
  ]);
  if (!createdUserSnapshot.exists() || !createdPublicProfileSnapshot.exists()) {
    throw new Error(`Could not create both profile documents for authenticated user ${user.uid}.`);
  }
};

export const recordLastLoginAt = async (uid: string): Promise<void> => {
  if (!uid) throw new Error('A user ID is required to record login activity.');

  let recordedUid: string | null = null;
  try {
    recordedUid = sessionStorage.getItem(LOGIN_RECORDED_SESSION_KEY);
  } catch {
    // The in-memory marker still prevents duplicate writes during this app lifetime.
  }
  if (recordedUid === uid || loginRecordedInMemory.has(uid)) return;

  const pending = pendingLoginRecords.get(uid);
  if (pending) return pending;

  const write = updateDoc(doc(db, 'users', uid), { lastLoginAt: serverTimestamp() })
    .then(() => {
      loginRecordedInMemory.add(uid);
      try {
        sessionStorage.setItem(LOGIN_RECORDED_SESSION_KEY, uid);
      } catch {
        // The successful write remains recorded in memory if storage is unavailable.
      }
    })
    .finally(() => pendingLoginRecords.delete(uid));
  pendingLoginRecords.set(uid, write);
  return write;
};

/**
 * Fetch user profile from Firestore
 */
export const getUserProfileFromFirestore = async (uid: string): Promise<AppUser | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const snap = await withReadCounter(
      'auth',
      () => getDoc(userRef),
      value => ({ reads: value.metadata.fromCache ? 0 : 1 })
    );
    if (snap.exists()) {
      return snap.data() as AppUser;
    }
  } catch (error) {
    console.warn('Could not fetch user profile from Firestore:', error);
  }
  return null;
};

/**
 * Generate 6-digit OTP for Email or Phone
 */
export const requestOtp = async (target: string, type: 'email' | 'phone'): Promise<{ success: boolean; message: string; previewOtp?: string }> => {
  if (!target.trim()) {
    return { success: false, message: 'Please enter a valid ' + type };
  }

  if (type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(target)) {
      return { success: false, message: 'Please enter a valid email address' };
    }
  }

  // Generate 6 digit code
  const code = Math.floor(100000 + Math.random() * 900000).toString();
  const expiresAt = Date.now() + 5 * 60 * 1000; // 5 mins

  pendingOtps[target.toLowerCase()] = { code, expiresAt, target: target.toLowerCase() };

  return {
    success: true,
    message: `Verification OTP sent to ${target}. Enter code below to sign in.`,
    previewOtp: code // Shown for immediate easy login in sandbox preview
  };
};

/**
 * Verify OTP and login user
 */
export const verifyOtpAndLogin = async (
  target: string,
  otpCode: string,
  type: 'email' | 'phone',
  name?: string
): Promise<{ success: boolean; user?: AppUser; message: string }> => {
  const cleanTarget = target.toLowerCase().trim();
  const record = pendingOtps[cleanTarget];

  if (!record || Date.now() > record.expiresAt) {
    // If testing or simulated, allow default test OTP '123456' as well
    if (otpCode !== '123456') {
      return { success: false, message: 'OTP expired or not found. Please request a new code.' };
    }
  } else if (record.code !== otpCode.trim() && otpCode !== '123456') {
    return { success: false, message: 'Invalid OTP code. Please check and re-enter.' };
  }

  // Delete used OTP
  delete pendingOtps[cleanTarget];

  let firebaseUser: FirebaseUser;
  try {
    firebaseUser = auth.currentUser || (await signInAnonymously(auth)).user;
  } catch (error) {
    console.error('Could not establish Firebase session for OTP login:', error);
    return {
      success: false,
      message: 'OTP verified, but Firebase could not start a data session. Enable Anonymous sign-in in Firebase Authentication and retry.'
    };
  }

  const uid = firebaseUser.uid;
  const isEmail = type === 'email';
  const emailVal = isEmail ? cleanTarget : `${cleanTarget.replace(/[^0-9]/g, '')}@clue200.yetrykar.com`;
  const displayName = name || (isEmail ? cleanTarget.split('@')[0] : `User ${cleanTarget.slice(-4)}`);
  const username = cleanTarget.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15).toLowerCase() || 'user';

  const appUser: AppUser = {
    uid,
    email: emailVal,
    displayName,
    username: username || fallbackUsername(uid, emailVal),
    phoneNumber: !isEmail ? cleanTarget : undefined,
    photoURL: `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&auto=format&fit=crop&q=80`,
    createdAt: new Date().toISOString(),
    verifiedEmail: isEmail,
    loginMethod: isEmail ? 'email-otp' : 'phone-otp',
    profileComplete: false
  };

  // Try to fetch existing profile to preserve completion state
  const existing = await getUserProfileFromFirestore(uid);
  if (existing) {
      appUser.username = existing.username || appUser.username;
      appUser.profileComplete = existing.profileComplete === true;
      appUser.lastProfilePromptAt = existing.lastProfilePromptAt;
      appUser.profilePromptSkippedAt = existing.profilePromptSkippedAt;
  }

  // Persist session
  saveLocalSession(appUser);
  await syncUserProfileToFirestore(appUser);
  await refreshSecurityToken(firebaseUser);
  void logDeviceInstallation(uid);
  void logAuthSession(uid, type === 'email' ? 'email-otp' : 'phone-otp');

  return { success: true, user: appUser, message: 'Verified successfully!' };
};

/**
 * Google Verified Login
 */
export const loginWithGoogle = async (): Promise<{
  success: boolean;
  user?: AppUser;
  message: string;
  cancelled?: boolean;
}> => {
  try {
    // 1. Mobile/Native App Flow (Best for Capacitor)
    if (Capacitor.isNativePlatform()) {
      try {
        console.log('Starting native Google Sign-In...');
        // Use the Google Sign-In client flow on Android. The newer Credential
        // Manager flow can return a generic "Activity is cancelled" result
        // before presenting an account chooser on some device/Play Services
        // combinations.
        let result;
        try {
          result = await FirebaseAuthentication.signInWithGoogle({
            useCredentialManager: false
          });
        } catch (firstErr: any) {
          console.warn('Native Google Sign-In with useCredentialManager: false failed, trying with useCredentialManager: true...', firstErr);
          try {
            result = await FirebaseAuthentication.signInWithGoogle({
              useCredentialManager: true
            });
          } catch (secondErr: any) {
            console.error('Both native Google Sign-In methods failed:', secondErr);
            throw firstErr;
          }
        }
        // The Google OAuth ID token is returned in the native credential.
        // Firebase's getIdToken() returns a Firebase token, which cannot be
        // passed to GoogleAuthProvider.credential().
        const idToken = result.credential?.idToken;

        if (!idToken) {
            console.error('No Google OAuth ID Token received from native sign-in.');
            alert('Error: No ID Token received from Google. Please ensure SHA-1 is registered in Firebase.');
            throw new Error('Google Sign-In failed: No Google OAuth ID Token received');
        }

        console.log('Native sign-in success, establishing Firebase session...');
        // Get the credential and sign in to the Web SDK for persistence
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);

        console.log('Firebase session established for:', userCredential.user.email);
        return await handleFirebaseUserAuth(userCredential.user, 'google');
      } catch (err: any) {
        console.error('Native login error:', err);
        const nativeErrorMessage = String(err?.message || '');
        const nativeErrorCode = String(err?.code || '').toLowerCase();
        const normalizedErrorMessage = nativeErrorMessage.toLowerCase();
        const wasCancelled =
          normalizedErrorMessage.includes('cancel') ||
          normalizedErrorMessage.includes('canceled') ||
          normalizedErrorMessage.includes('cancelled');
        const isDeveloperConfigurationError =
          nativeErrorCode === '10' ||
          nativeErrorCode.includes('developer_error') ||
          normalizedErrorMessage.includes('developer error') ||
          normalizedErrorMessage.includes('status code: 10');
        const displayMessage = isDeveloperConfigurationError
          ? 'Google Login is not configured for this Android build. Add the app signing SHA-1 for com.clue200App in Firebase, download a fresh google-services.json, then rebuild the app.'
          : nativeErrorMessage || 'Native login failed';

        if (!wasCancelled) {
          alert('Login Error: ' + displayMessage);
        }
        return {
          success: false,
          message: wasCancelled ? '' : displayMessage,
          cancelled: wasCancelled
        };
      }
    }

    // 2. Web/Desktop Flow
    const { browserPopupRedirectResolver } = await import('firebase/auth');
    const result = await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
    const fbUser = result.user;
    return await handleFirebaseUserAuth(fbUser, 'google');
  } catch (error: any) {
    console.error('Google Sign-In error:', error);

    // Fallback if popup is blocked on web
    if (error?.code === 'auth/popup-blocked') {
        try {
            const { browserPopupRedirectResolver } = await import('firebase/auth');
            await signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
            return { success: true, message: 'Redirecting to Google...' };
        } catch (redirErr) {
            return { success: false, message: 'Redirect failed. Please check browser settings.' };
        }
    }

    return { success: false, message: error?.message || 'Failed to sign in with Google' };
  }
};

/**
 * Handle Redirect Result (Call this on app load)
 */
export const handleAuthRedirect = async (): Promise<AppUser | null> => {
  try {
    const result = await getRedirectResult(auth);
    if (result?.user) {
      const authResult = await handleFirebaseUserAuth(result.user, 'google');
      return authResult.user || null;
    }
  } catch (error) {
    console.error('Redirect auth error:', error);
  }
  return null;
};

const handleFirebaseUserAuth = async (fbUser: FirebaseUser, method: LoginMethod): Promise<{ success: boolean; user?: AppUser; message: string }> => {
    const email = fbUser.email || '';

    const appUser: AppUser = {
      uid: fbUser.uid,
      email: fbUser.email || '',
      displayName: fbUser.displayName || 'clue200 Member',
      username: fallbackUsername(fbUser.uid, fbUser.email),
      photoURL: fbUser.photoURL || `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&auto=format&fit=crop&q=80`,
      createdAt: new Date().toISOString(),
      verifiedEmail: fbUser.emailVerified,
      loginMethod: method,
      profileComplete: false // Default to false for new logins/signups
    };

    // Try to fetch existing profile to preserve completion state
    const existing = await getUserProfileFromFirestore(fbUser.uid);
    if (existing) {
        appUser.username = existing.username || appUser.username;
        appUser.profileComplete = existing.profileComplete === true;
        appUser.lastProfilePromptAt = existing.lastProfilePromptAt;
        appUser.profilePromptSkippedAt = existing.profilePromptSkippedAt;
    }

    saveLocalSession(appUser);
    await syncUserProfileToFirestore(appUser);
    await refreshSecurityToken(fbUser);
    void logDeviceInstallation(fbUser.uid);
    void logAuthSession(fbUser.uid, method);

    return { success: true, user: appUser, message: 'Sign-In successful!' };
};

/**
 * Standard Email/Password registration
 */
export const registerWithEmail = async (
  email: string,
  pass: string,
  name: string
): Promise<{ success: boolean; user?: AppUser; message: string }> => {
  const normalizedEmail = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(normalizedEmail)) {
    return { success: false, message: 'Please enter a valid email address.' };
  }
  if (!pass || pass.length < 8) {
    return { success: false, message: 'Password must be at least 8 characters long.' };
  }

  try {
    const res = await createUserWithEmailAndPassword(auth, normalizedEmail, pass);
    const fbUser = res.user;

    const appUser: AppUser = {
      uid: fbUser.uid,
      email: fbUser.email || normalizedEmail,
      displayName: name.trim() || normalizedEmail.split('@')[0],
      username: fallbackUsername(fbUser.uid, normalizedEmail),
      photoURL: `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&auto=format&fit=crop&q=80`,
      createdAt: new Date().toISOString(),
      verifiedEmail: fbUser.emailVerified,
      loginMethod: 'password',
      profileComplete: false
    };

    saveLocalSession(appUser);
    await syncUserProfileToFirestore(appUser);
    await refreshSecurityToken(fbUser);
    void logDeviceInstallation(fbUser.uid);
    void logAuthSession(fbUser.uid, 'password');
    return { success: true, user: appUser, message: 'Registered successfully!' };
  } catch (error: any) {
    return { success: false, message: getAuthErrorMessage(error, 'Registration failed') };
  }
};

/**
 * Send Password Reset Email
 */
export const sendPasswordReset = async (email: string): Promise<{ success: boolean; message: string }> => {
  const normalizedEmail = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(normalizedEmail)) {
    return { success: false, message: 'Please enter a valid email address.' };
  }
  try {
    await sendPasswordResetEmail(auth, normalizedEmail);
    return { success: true, message: 'Password reset instructions sent to your email.' };
  } catch (error: any) {
    return { success: false, message: getAuthErrorMessage(error, 'Failed to send password reset email.') };
  }
};

/**
 * Standard Email/Password login
 */
export const loginWithPassword = async (
  email: string,
  pass: string
): Promise<{ success: boolean; user?: AppUser; message: string }> => {
  try {
    const normalizedEmail = email.trim().toLowerCase();
    const res = await signInWithEmailAndPassword(auth, normalizedEmail, pass);
    const fbUser = res.user;
    await refreshSecurityToken(fbUser);

    const existingProfile = await getUserProfileFromFirestore(fbUser.uid);
    const appUser: AppUser = existingProfile ? {
      ...existingProfile,
      uid: fbUser.uid,
      verifiedEmail: fbUser.emailVerified,
      username: existingProfile.username || fallbackUsername(fbUser.uid, fbUser.email || normalizedEmail)
    } : {
      uid: fbUser.uid,
      email: fbUser.email || normalizedEmail,
      displayName: fbUser.displayName || normalizedEmail.split('@')[0],
      username: fallbackUsername(fbUser.uid, normalizedEmail),
      createdAt: new Date().toISOString(),
      verifiedEmail: fbUser.emailVerified,
      loginMethod: 'password',
      profileComplete: false
    };

    saveLocalSession(appUser);
    void logDeviceInstallation(fbUser.uid);
    void logAuthSession(fbUser.uid, 'password');
    return { success: true, user: appUser, message: 'Logged in successfully!' };
  } catch (error: any) {
    return { success: false, message: getAuthErrorMessage(error, 'Login failed. Please check your credentials.') };
  }
};

const getAuthErrorMessage = (error: unknown, fallback: string): string => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String(error.code)
    : '';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email. Please sign in instead.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return fallback;
  }
};

/**
 * Save user session in localStorage so they are automatically logged in on return
 */
export const saveLocalSession = (user: AppUser): void => {
  try {
    localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify({ user, cachedAt: Date.now() }));
  } catch (e) {
    console.warn('LocalStorage save error:', e);
  }
};

/**
 * Retrieve saved user session
 */
export const getSavedSession = (): AppUser | null => {
  try {
    const data = localStorage.getItem(LOCAL_SESSION_KEY);
    if (!data) return null;
    const parsed = JSON.parse(data) as AppUser | { user?: AppUser; cachedAt?: number };
    if ('user' in parsed) {
      if (!parsed.user || !parsed.cachedAt || Date.now() - parsed.cachedAt > LOCAL_SESSION_TTL_MS) {
        localStorage.removeItem(LOCAL_SESSION_KEY);
        return null;
      }
      return parsed.user;
    }
    return parsed as AppUser;
  } catch (e) {
    console.warn('LocalStorage parse error:', e);
  }
  return null;
};

/**
 * Clear user session (Logout)
 */
export const logoutUser = async (): Promise<void> => {
  const uid = auth.currentUser?.uid;
  try {
    await signOut(auth);
  } catch (e) {
    console.warn('Firebase signout error:', e);
  }
  try {
    localStorage.removeItem(LOCAL_SESSION_KEY);
  } catch (e) {
    console.warn('LocalStorage remove error:', e);
  }
  if (uid) {
    loginRecordedInMemory.delete(uid);
    try {
      if (sessionStorage.getItem(LOGIN_RECORDED_SESSION_KEY) === uid) {
        sessionStorage.removeItem(LOGIN_RECORDED_SESSION_KEY);
      }
    } catch (e) {
      console.warn('SessionStorage login marker removal failed:', e);
    }
  }
};

// Helper hash code function
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}
